import {
  getObjectSchema,
  getAllObjectTypes,
  getAvailableEvents,
} from '../../Hasp/properties/v2/wireFormat.js';
import { DesignerSchemaAdapter } from '../../components/hassbuilder/metadata/DesignerSchemaAdapter.js';

export { getAvailableEvents };

/**
 * WidgetAutomationMeta
 *
 * Reads automation metadata from v2 widget schemas.
 *
 * Each interactive widget schema may declare an `automation` field:
 *   {
 *     runtimeVariables: WidgetRuntimeVariable[]  — variables available in handlers
 *     actionDomains: string[]                    — recommended provider domains
 *     actionHints: object                        — Designer-side HA action hints
 *     supportedActions/dataSuggestions           — legacy compatibility aliases
 *     preferredDataParameters: object             — optional per-widget ordering
 *   }
 *
 * For schemas without an explicit `automation` field, metadata is derived
 * automatically from existing schema fields so older widgets still produce
 * a valid toolbox without any schema changes.
 *
 * Adding a new widget with an `automation` field requires zero editor changes —
 * the Automation Editor derives its UI entirely from this metadata.
 *
 * @typedef {{
 *   name: string,
 *   type: 'int'|'string'|'bool'|'color',
 *   label: string,
 *   optionsFrom?: string,
 * }} WidgetRuntimeVariable
 *   optionsFrom — when set, this variable's value is constrained to the
 *   widget's own configured option list (e.g. dropdown/roller's newline-
 *   delimited `options` property) rather than free-form input. The
 *   Automation Editor reads `node[optionsFrom]` at edit time and renders an
 *   enum picker of those options instead of a text field.
 *
 * @typedef {{
 *   objType: string,
 *   triggers: string[],
 *   events: object[],
 *   runtimeVariables: WidgetRuntimeVariable[],
 *   supportedActions: string[],
 *   recommendedActionDomains: string[],
 *   dataSuggestions?: object,
 *   preferredDataParameters?: object,
 *   legacyRowIndexVariable?: string,
 *   legacyTrigger?: string,
 * }} WidgetAutomationMeta
 *   legacyRowIndexVariable — set only by widgets (e.g. msgbox) whose legacy
 *   hass_config.events[] rows are keyed by row position rather than by
 *   event type — one row per button, not per event. When set, the migration
 *   (LegacyEventsToAst.js) skips the eventType picker requirement and adds a
 *   CompareCondition per row comparing this variable to the row's 0-based
 *   index instead.
 *   legacyTrigger — legacy event used when old rows had no event picker; this
 *   is migration compatibility metadata, not event availability.
 */

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Get automation metadata for a widget from its v2 schema object.
 *
 * Returns null if the widget has no triggers and no declared automation field
 * (i.e. it cannot participate in automations).
 *
 * @param {string} objType
 * @param {object} schema   — v2 schema object
 * @returns {WidgetAutomationMeta|null}
 */
export function getWidgetAutomationMeta(objType, schema) {
  if (!schema) return null;

  // Event availability always comes from schema.events. The optional
  // automation block only contributes editor-specific metadata; its former
  // triggers field is deliberately ignored so it cannot drift from the V2
  // event declaration.
  const derived = _deriveFromSchema(objType, schema);
  const meta = schema.automation
    ? {
        ...derived,
        objType,
        ...schema.automation,
        // Event payloads and their runtime variables are canonical in schema.events.
        // The explicit automation.runtimeVariables block remains a fallback inside
        // DesignerSchemaAdapter for older schemas, but must not override typed data.
        runtimeVariables: derived.runtimeVariables,
        triggers: derived.triggers,
        supportedActions: derived.recommendedActionDomains,
        recommendedActionDomains: derived.recommendedActionDomains,
        actionHints: derived.actionHints,
      }
    : derived;
  // Only return derived meta if the widget actually fires events
  return meta.triggers.length > 0 ? meta : null;
}
/**
 * Get automation metadata for a widget by object type string.
 *
 * @param {string} objType
 * @returns {WidgetAutomationMeta|null}
 */
export function getAutomationMetaByType(objType) {
  const schema = getObjectSchema(objType);
  return getWidgetAutomationMeta(objType, schema);
}

/**
 * Return all widget types that can participate in automations (have triggers).
 *
 * @returns {string[]}
 */
export function getAutomatableWidgetTypes() {
  return getAllObjectTypes().filter(type => {
    const schema = getObjectSchema(type);
    return schema && getAvailableEvents(schema).length > 0;
  });
}

// ── Derivation ────────────────────────────────────────────────────────────────

/** @private */
function _deriveFromSchema(objType, schema) {
  const adapter = new DesignerSchemaAdapter(schema, objType);
  const events = adapter.getEvents();
  const triggers = events.map(event => event.name);
  const runtimeVariables = adapter.getRuntimeVariables();
  const recommendedActionDomains = adapter.getRecommendedActionDomains();
  const actionHints = adapter.getActionHints();

  return {
    objType,
    triggers,
    events,
    runtimeVariables,
    supportedActions: recommendedActionDomains,
    recommendedActionDomains,
    actionHints,
  };
}
